/**
 * 2FA login challenge page (TASK-631) — /login/2fa
 *
 * Reached from LoginPage when the backend answers a valid password with
 * {status: "2fa_required" | "2fa_enrollment_required", challenge_token}.
 * Router state carries {challengeToken, status, email, from}; landing here
 * without it (deep link, refresh — the 5-min challenge is gone) bounces to /login.
 *
 * - 2fa_required: enter TOTP or backup code → verify-login → session.
 * - 2fa_enrollment_required: TwoFAEnrollFlow first, then the NEXT code
 *   completes login (the confirm code is replay-guarded server-side).
 */

import React, { useState } from 'react';
import { useNavigate, useLocation, Navigate } from 'react-router-dom';
import { Loader2, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';
import KielisLogo from '../../assets/KielisLogo-no-bg.png';
import { httpClient } from '../lib/httpClient';
import { useLogin, LoginResponse } from '../hooks/useLogin';
import TwoFAEnrollFlow from '../components/auth/TwoFAEnrollFlow';

interface ChallengeState {
  challengeToken: string;
  status: '2fa_required' | '2fa_enrollment_required';
  email: string;
  from?: string;
}

export default function TwoFAChallengePage() {
  const navigate = useNavigate();
  const location = useLocation();
  const state = location.state as ChallengeState | null;
  const { finalizeSession } = useLogin();

  const [needsEnrollment, setNeedsEnrollment] = useState(
    state?.status === '2fa_enrollment_required'
  );
  const [justEnrolled, setJustEnrolled] = useState(false);
  const [code, setCode] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  if (!state?.challengeToken) {
    return <Navigate to="/login" replace />;
  }

  const handleVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!code.trim()) {
      setError('Enter your authentication code');
      return;
    }
    setIsLoading(true);
    try {
      const response = await httpClient.post<LoginResponse>('/api/auth/2fa/verify-login', {
        challenge_token: state.challengeToken,
        code: code.trim(),
      });
      await finalizeSession(response, state.email);
      toast.success('Welcome to Kielis');
      navigate(state.from || '/dashboard', { replace: true });
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Invalid code. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-kielis-glacier to-slate-100 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-6">
          <img src={KielisLogo} alt="Kielis" className="h-14 mx-auto mb-4" />
          <div className="w-12 h-12 mx-auto mb-3 bg-amber-50 rounded-full flex items-center justify-center">
            <ShieldCheck size={24} className="text-kielis-gold" />
          </div>
          <h1 className="text-2xl font-bold text-kielis-navy mb-1">
            {needsEnrollment ? 'Set up two-factor authentication' : 'Two-factor authentication'}
          </h1>
          <p className="text-slate-600 text-sm">
            {needsEnrollment
              ? 'Your organization requires a second factor to sign in'
              : `Signed in as ${state.email} — enter the code from your authenticator app`}
          </p>
        </div>

        <div className="bg-white rounded-xl shadow-lg p-6 border border-slate-200">
          {needsEnrollment ? (
            <TwoFAEnrollFlow
              challengeToken={state.challengeToken}
              onEnabled={() => {
                setNeedsEnrollment(false);
                setJustEnrolled(true);
              }}
            />
          ) : (
            <>
              {justEnrolled && (
                <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-800">
                  Almost done — enter the <span className="font-medium">next</span> code from
                  your authenticator app to finish signing in.
                </div>
              )}
              {error && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
                  {error}
                </div>
              )}
              <form onSubmit={handleVerify} className="space-y-4">
                <div>
                  <label htmlFor="code" className="block text-sm font-medium text-gray-700 mb-1">
                    Authentication code
                  </label>
                  <input
                    id="code"
                    type="text"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    value={code}
                    onChange={(e) => setCode(e.target.value)}
                    placeholder="123456 or backup code"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm font-mono tracking-widest focus:outline-none focus:ring-2 focus:ring-kielis-gold focus:border-transparent"
                    disabled={isLoading}
                    autoFocus
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    6-digit code, or a backup code (xxxx-xxxx) if you lost your device.
                  </p>
                </div>
                <button
                  type="submit"
                  disabled={isLoading || !code.trim()}
                  className="w-full py-3 px-4 bg-kielis-navy text-white font-medium rounded-lg hover:bg-opacity-90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {isLoading ? (
                    <>
                      <Loader2 size={18} className="animate-spin" />
                      Verifying...
                    </>
                  ) : (
                    'Verify'
                  )}
                </button>
              </form>
            </>
          )}

          <p className="mt-4 text-center text-xs text-gray-500">
            <button onClick={() => navigate('/login')} className="text-kielis-navy hover:underline">
              Back to login
            </button>
          </p>
        </div>
      </div>
    </div>
  );
}
